import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import { DropdownItem } from 'app/components/DropdownSelect';

interface UseDropdownSelectProps {
  queryKey: string;
  fetchItems?: (page: number) => Promise<{ items: DropdownItem[]; hasMore: boolean }>;
  initialItems: DropdownItem[];
  onSubmit?: (selectedItems: DropdownItem[]) => void;
}

const useDropdownSelect = ({
  queryKey,
  fetchItems,
  initialItems = [],
  onSubmit,
}: UseDropdownSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Record<string | number, boolean>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery(
    [queryKey],
    async ({ pageParam = 1 }) => {
      if (fetchItems) {
        return fetchItems(pageParam);
      }
      return { items: [], hasMore: false };
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.hasMore ? 'nextPage' : undefined;
      },
      enabled: !!fetchItems && isOpen,
    },
  );

  const allItems = useMemo(() => {
    if (!fetchItems) {
      return initialItems;
    }
    return [...initialItems, ...(data?.pages.flatMap((page) => page.items) || [])];
  }, [initialItems, data, fetchItems]);

  useEffect(() => {
    if (!isOpen || !loadMoreRef.current || !fetchItems) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage, fetchItems]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Khởi tạo selected items
  useEffect(() => {
    const initialSelected: Record<string | number, boolean> = {};
    allItems.forEach((item) => {
      if (item.selected) {
        initialSelected[item.id] = true;
      }
    });
    setSelectedItems(initialSelected);
  }, [allItems]);

  const handleToggleDropdown = () => setIsOpen(!isOpen);

  const handleCheckboxChange = (id: string | number) => {
    setSelectedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (onSubmit) {
        const selected = allItems.filter((item) => selectedItems[item.id]);
        await onSubmit(selected);
        setIsOpen(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => setIsOpen(false);

  return {
    isOpen,
    isSubmitting,
    allItems,
    isLoading: isLoading && fetchItems !== undefined,
    isFetchingNextPage: isFetchingNextPage && fetchItems !== undefined,
    loadMoreRef,
    dropdownRef,
    selectedItems,
    handleCheckboxChange,
    handleSubmit,
    handleCancel,
    handleToggleDropdown,
  };
};

export default useDropdownSelect;
